import { KurooApiError } from "@kuroo/sdk";
import type { AvailableSlot, BookingExtraSelection, PriceQuoteBreakdown, RecurringFrequency, RecurringRule, ServiceSummary } from "@kuroo/contracts";
import type { CatalogExtra } from "@kuroo/sdk";
import { escapeHtml } from "./booking-utils";

export function recurringRuleFromForm(form: HTMLFormElement): RecurringRule {
  const frequency = (form.elements.namedItem("recurrenceFrequency") as HTMLSelectElement | null)?.value as RecurringFrequency | undefined;
  const interval = Math.max(1, Number((form.elements.namedItem("recurrenceInterval") as HTMLInputElement | null)?.value || 1));
  if (!frequency || !Number.isInteger(interval) || interval > 52) throw new Error("Elegí una frecuencia e intervalo válidos.");
  const rule: RecurringRule = { frequency, interval };
  if (frequency === "weekly") {
    const weeklyDays = [...form.querySelectorAll<HTMLInputElement>('input[name="weeklyDay"]:checked')].map((input) => Number(input.value));
    if (!weeklyDays.length) throw new Error("Seleccioná al menos un día de la semana.");
    rule.weeklyDays = weeklyDays;
  }
  if (frequency === "monthly") {
    const monthlyMode = (form.elements.namedItem("monthlyMode") as HTMLSelectElement | null)?.value === "weekday_position" ? "weekday_position" : "day_of_month";
    rule.monthlyMode = monthlyMode;
    if (monthlyMode === "day_of_month") {
      const monthlyDay = Number((form.elements.namedItem("monthlyDay") as HTMLInputElement | null)?.value || 1);
      if (!Number.isInteger(monthlyDay) || monthlyDay < 1 || monthlyDay > 31) throw new Error("El día mensual debe estar entre 1 y 31.");
      rule.monthlyDay = monthlyDay;
    } else {
      rule.monthlyWeekday = Number((form.elements.namedItem("monthlyWeekday") as HTMLSelectElement | null)?.value || 0);
      rule.monthlyWeek = Number((form.elements.namedItem("monthlyWeek") as HTMLSelectElement | null)?.value || 1);
    }
  }
  return rule;
}

export function selectedExtrasFromForm(form: HTMLFormElement, extras: CatalogExtra[], recurring = false): BookingExtraSelection[] {
  const prefix = recurring ? "recurring-extra-" : "extra-";
  return extras.filter((extra) => {
    const input = form.querySelector<HTMLInputElement>(`input[name="${prefix}${CSS.escape(extra.id)}"]`);
    return Boolean(input?.checked);
  }).map((extra) => ({ extraId: extra.id, quantity: 1 }));
}

export function recurringParticipantsFromForm(form: HTMLFormElement): Array<{ name: string; email?: string }> {
  return [...form.querySelectorAll<HTMLElement>("[data-recurring-participant]")].map((row) => {
    const name = (row.querySelector<HTMLInputElement>('input[name="recurringParticipantName"]')?.value ?? "").trim();
    const email = (row.querySelector<HTMLInputElement>('input[name="recurringParticipantEmail"]')?.value ?? "").trim();
    return { name, ...(email ? { email } : {}) };
  }).filter((participant) => participant.name.length > 0);
}

export function recurringCountFromForm(form: HTMLFormElement, fallback: number): number {
  const raw = Number((form.elements.namedItem("recurrenceCount") as HTMLInputElement | null)?.value);
  if (!Number.isFinite(raw)) return fallback;
  return Math.min(52, Math.max(1, Math.trunc(raw)));
}

export function couponCodeFromForm(form: HTMLFormElement): string | undefined {
  const review = form.querySelector<HTMLInputElement>("[data-review-coupon]");
  const legacy = form.elements.namedItem("couponCode") as HTMLInputElement | null;
  const value = (review?.value ?? legacy?.value ?? "").trim();
  return value || undefined;
}

export function oneOffEndAt(form: HTMLFormElement, slot: AvailableSlot, service: ServiceSummary, extras: CatalogExtra[]): string {
  const extraDuration = selectedExtrasFromForm(form, extras).reduce((total, selected) => {
    const extra = extras.find((item) => item.id === selected.extraId);
    return total + (extra?.duration_minutes ?? 0) * selected.quantity;
  }, 0);
  return new Date(Date.parse(slot.startAt) + (service.durationMinutes + extraDuration) * 60_000).toISOString();
}

export function formatMoney(cents: number, currency: string, locale = "es-AR"): string {
  try { return new Intl.NumberFormat(locale, { style: "currency", currency }).format(cents / 100); }
  catch { return `${(cents / 100).toFixed(2)} ${currency}`; }
}

export function renderStaticPriceTooltip(amountCents: number, currency: string, description: string, label = "Detalle del precio", locale = "es-AR"): string {
  return renderPriceTooltip(amountCents, currency, null, label, description, locale);
}

export function renderPriceTooltip(amountCents: number, currency: string, breakdown?: PriceQuoteBreakdown | null, label = "Detalle del precio", extraDescription?: string, locale = "es-AR"): string {
  const lines = breakdown?.lines?.length
    ? `<ul>${breakdown.lines.map((line) => `<li>${escapeHtml(line.label)} × ${line.quantity}: <strong>${escapeHtml(formatMoney(line.totalCents, currency, locale))}</strong></li>`).join("")}</ul>`
    : "";
  const dynamic = breakdown?.dynamicAdjustmentCents ?? 0;
  const discount = breakdown?.discountCents ?? 0;
  const tax = breakdown?.taxCents ?? 0;
  const details = breakdown
    ? `${lines}<footer><span>Precio base <b>${escapeHtml(formatMoney(breakdown.subtotalCents - dynamic, currency, locale))}</b></span>${dynamic ? `<span class="${dynamic > 0 ? "positive" : "negative"}">Ajuste dinámico <b>${escapeHtml(formatMoney(dynamic, currency, locale))}</b></span>` : ""}${breakdown.appliedRules.map((rule) => `<span>${escapeHtml(rule.label || "Regla de precio")} <b>${escapeHtml(rule.reason)}</b></span>`).join("")}${discount ? `<span class="negative">Cupón / descuento <b>${escapeHtml(formatMoney(-discount, currency, locale))}</b></span>` : ""}${tax ? `<span>Impuestos <b>${escapeHtml(formatMoney(tax, currency, locale))}</b></span>` : ""}<span>Total <b>${escapeHtml(formatMoney(breakdown.totalCents, currency, locale))}</b></span><span>Depósito <b>${escapeHtml(formatMoney(breakdown.depositCents, currency, locale))}</b></span></footer>`
    : `<p>${escapeHtml(extraDescription ?? "Precio configurado por el negocio.")}</p>`;
  return `<span class="price-with-tooltip" tabindex="0" role="img" aria-label="${escapeHtml(`${label}: ${formatMoney(amountCents, currency, locale)}`)}" aria-expanded="false" data-price-tooltip><span>${escapeHtml(formatMoney(amountCents, currency, locale))}</span><span class="price-tooltip" role="tooltip"><strong>${escapeHtml(label)}</strong>${details}</span></span>`;
}

export function formatSlot(slot: AvailableSlot, locale = "es-ES"): string {
  const formatter = new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit", timeZone: slot.timezone ?? undefined });
  return `${formatter.format(new Date(slot.startAt))}–${formatter.format(new Date(slot.endAt))}`;
}

export function formatOccurrenceDateTime(value: string, timezone: string, locale = "es-ES"): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: "full", timeStyle: "short", timeZone: timezone }).format(date);
  } catch {
    return date.toLocaleString(locale);
  }
}

export function adjustmentReasonText(reason: string | null | undefined): string {
  switch (reason) {
    case "DATE_MOVED_NEXT_AVAILABLE":
      return "No había disponibilidad para el horario solicitado; se asignó la próxima fecha disponible según la regla de recurrencia.";
    case "DATE_MOVED_PREVIOUS_AVAILABLE":
      return "No había disponibilidad para el horario solicitado; se asignó la fecha disponible anterior según la regla de recurrencia.";
    case "TIME_MOVED_AVAILABLE_SLOT":
      return "No había disponibilidad para el horario solicitado; se asignó otro horario disponible.";
    default:
      return "No había disponibilidad para el horario solicitado; se asignó una alternativa disponible.";
  }
}

export function errorMessage(error: unknown): string {
  if (error instanceof KurooApiError) return `${error.message} (${error.code})`;
  return error instanceof Error ? error.message : "No se pudo conectar con la disponibilidad.";
}
